import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CourtChangeRequestDecision } from '@ee/common/enums';
import {
  CourtChangeDecisionResult,
  CourtChangeRequest,
  CourtChangeRequestSubmission,
  CourtChangeRequestSubmissionResult,
  CourtInbox
} from '@ee/common/models';

/**
 * Court change request API (EE-386).
 *
 * Two audiences, two base paths:
 *  - In-app submission ({@link submit}) posts to `/api/court-change-requests`; the backend gates it to
 *    an attorney admin and can only open a PENDING request — it never writes a court.
 *  - The admin inbox calls ({@link getInbox}, {@link getRequests}, {@link getPendingCount},
 *    {@link approve}, {@link decline}) live under `/api/court-directory-sync`, alongside the existing
 *    proposal endpoints ({@link CourtSyncService}), and are GLOBAL_ADMIN-only server-side. Approving a
 *    request is the single action that creates or updates a court.
 *
 * Bodies are sent/consumed in snake_case to match the backend serialization (api-conventions.md).
 */
@Injectable({ providedIn: 'root' })
export class CourtChangeRequestService {
  private readonly submissionUrl: string;
  private readonly adminUrl: string;

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment: { api_prefix: string }
  ) {
    this.submissionUrl = `${this.environment.api_prefix}api/court-change-requests`;
    this.adminUrl = `${this.environment.api_prefix}api/court-directory-sync`;
  }

  /**
   * Submit an in-app court change request. The source is forced to IN_APP and the decision to PENDING
   * server-side, so the result confirms the request was opened for review — never an immediate change.
   */
  submit(submission: CourtChangeRequestSubmission): Observable<CourtChangeRequestSubmissionResult> {
    return this.http.post<CourtChangeRequestSubmissionResult>(this.submissionUrl, submission);
  }

  /**
   * The unified admin inbox: the field-level proposals and the whole-court requests as two separate
   * lists (never merged), plus each pending count.
   */
  getInbox(): Observable<CourtInbox> {
    return this.http.get<CourtInbox>(`${this.adminUrl}/inbox`);
  }

  /**
   * List whole-court change requests, filtered by decision (default PENDING) and optionally by state.
   */
  getRequests(
    state?: string,
    decision: CourtChangeRequestDecision = CourtChangeRequestDecision.PENDING
  ): Observable<CourtChangeRequest[]> {
    let params = new HttpParams().set('decision', decision);
    if (state) {
      params = params.set('state', state);
    }
    return this.http.get<CourtChangeRequest[]>(`${this.adminUrl}/requests`, { params });
  }

  /**
   * The pending count for the inbox nav badge. The `/requests/pending-count` endpoint returns the
   * COMBINED total — pending whole-court requests plus pending field-level sync proposals — so the
   * caller (the admin nav badge) can read it once rather than summing two calls.
   */
  getPendingCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.adminUrl}/requests/pending-count`);
  }

  /** Approve a request: create the new court or apply a targeted update, then mark it APPROVED. */
  approve(id: string): Observable<CourtChangeDecisionResult> {
    return this.http.post<CourtChangeDecisionResult>(`${this.adminUrl}/requests/${id}/approve`, null);
  }

  /** Decline a request: write no court, record an optional reason, and mark it DECLINED. */
  decline(id: string, note?: string): Observable<CourtChangeDecisionResult> {
    return this.http.post<CourtChangeDecisionResult>(`${this.adminUrl}/requests/${id}/decline`, { note });
  }
}
